import { DataProvider } from "@/api/DataProvider";
import { ParticipantTypeEnum } from "@/enums/RecapEnums";
import { ModifyActionItems, ModifyDecisions } from "@/interfaces/IRecap";
import { formatDurationText, formatTime } from "@/middleware/Date";
import i18n from "@/shared/i18n";

const handleRequest = async <T>(requestFn: () => Promise<T>): Promise<T> => {
  try {
    return await requestFn();
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || error?.message || "Failed"
    );
  }
};

export const fetchMeetDataByKey = ({ meetingId }: { meetingId: string }) =>
  handleRequest(() => DataProvider.getList(`v1/meets/${meetingId}`, ""));

export const fetchSessionsByMeetKey = ({
  meetingKey,
}: {
  meetingKey: string;
}) =>
  handleRequest(() =>
    DataProvider.getList(`v1/meets/key/${meetingKey}/sessions`, "").then(
      (r: any) => r?.data || []
    )
  );

export const fetchSessionDataBySessionId = ({
  sessionId,
}: {
  sessionId: string;
}) =>
  handleRequest(() =>
    DataProvider.getList(`v1/sessions/${sessionId}`, "").then(
      (r: any) => r || { totalCount: 0, data: [] }
    )
  );

export const fetchRecordingsBySessionId = ({
  sessionId,
}: {
  sessionId: string;
}) =>
  handleRequest(() =>
    DataProvider.getList(`v1/sessions/${sessionId}/recordings`, "").then(
      (r: any) => r || []
    )
  );

export const fetchParticipantsBySessionId = ({
  sessionId,
}: {
  sessionId: string;
}) =>
  handleRequest(() =>
    DataProvider.getList(`v1/sessions/${sessionId}/participants`, "").then(
      (result: any) => {
        const data = (result?.data || [])
          .map((item: any) => {
            const userId = item?.userId || item?.user?.id || null;
            const fullName = item?.user?.fullName || item?.nickname || "";

            if (!fullName) return null;

            return {
              ...item,
              label: fullName,
              value: userId || fullName,
              userId,
              assigneeName: fullName,
            };
          })
          .filter(Boolean);

        const attendeesList = data.filter(
          (item: any) => item?.type === ParticipantTypeEnum.Attendee
        );

        const absentsList = data.filter(
          (item: any) => item?.type === ParticipantTypeEnum.Absent
        );

        const guestsList = data.filter(
          (item: any) => item?.type === ParticipantTypeEnum.Guest
        );

        const joinedViaCallList = data.filter(
          (item: any) => item?.type === ParticipantTypeEnum.JoinViaCall
        );

        const organizerItem = data.find((item: any) => item?.isOrganizer);

        return {
          totalCount: result?.totalCount || 0,
          data,
          stats: {
            organizer:
              organizerItem?.user?.fullName || organizerItem?.nickname || "-",
            attendedCount: attendeesList.length,
            invitedCount: attendeesList.length + absentsList.length,
            guestCount: guestsList.length,
            joinedViaCallCount: joinedViaCallList.length,
          },
        };
      }
    )
  );

export const fetchKeypointsBySessionId = ({
  sessionId,
}: {
  sessionId: string;
}) =>
  handleRequest(() =>
    DataProvider.getList(`v1/sessions/${sessionId}/keypoints`, "").then(
      (r: any) => r || []
    )
  );

export const fetchActionItemsBySessionId = ({
  sessionId,
  includeDeleted,
}: {
  sessionId: string;
  includeDeleted?: boolean;
}) =>
  handleRequest(() =>
    DataProvider.getList(
      `v1/sessions/${sessionId}/action-items`,
      includeDeleted === undefined ? "" : { includeDeleted }
    ).then((r: any) => r || [])
  );

export const undoDeleteActionItem = ({ id }: { id: string | number }) =>
  handleRequest(() =>
    DataProvider.getList(`v1/action-items/${id}/undo-delete`)
  );

export const modifyActionItem = ({
  id,
  data,
  participants = [],
}: ModifyActionItems) =>
  handleRequest(() => {
    const selectedParticipant = participants.find(
      (item: any) => item?.value === data?.assigneeName
    );

    const payload = {
      ...data,
      assigneeName:
        selectedParticipant?.assigneeName ||
        selectedParticipant?.label ||
        data?.assigneeName ||
        "",
      userId: selectedParticipant?.userId || null,
    };

    if (id) {
      return DataProvider.update(`v1/action-items`, {
        ...payload,
        id,
      });
    }

    return DataProvider.post(`v1/action-items`, payload);
  });

export const getActionItemById = (id: string) =>
  handleRequest(() =>
    DataProvider.getOne(`v1/action-items`, { id }).then((r: any) => r || null)
  );

export const deleteActionItem = ({
  id,
}: {
  sessionId: string;
  id: string | number;
}) => handleRequest(() => DataProvider.delete(`v1/action-items/${id}`));

export const fetchMeetRecap = ({ meetingId }: { meetingId: string }) =>
  handleRequest(() => DataProvider.getList(`v1/meets/${meetingId}/recap`, ""));

export const fetchOverviewBySessionId = ({
  sessionId,
}: {
  sessionId: string;
}) =>
  handleRequest(() =>
    DataProvider.getList(`v1/sessions/${sessionId}/overview`, "").then(
      (result: any) => {
        const overview = result || { totalCount: 0, data: [] };

        return {
          ...overview,

          duration: {
            ...overview?.duration,
            durationText:
              formatDurationText((overview?.duration?.duration ?? 0) * 60) ||
              "-",
            description:
              overview?.duration?.startAt && overview?.duration?.endAt
                ? i18n.t("recap.durationDescription", {
                    start: formatTime(overview?.duration?.startAt),
                    end: formatTime(overview?.duration?.endAt),
                  })
                : "-",
          },

          firstJoin: {
            ...overview?.firstJoin,
            time: overview?.firstJoin?.date
              ? formatTime(overview?.firstJoin?.date)
              : "--:--",
            delayTime: formatDurationText(overview?.firstJoin?.delay),
            description:
              overview?.firstJoin?.delay > 0
                ? i18n.t("recap.firstJoinDescription", {
                    delayTime: formatDurationText(overview?.firstJoin?.delay),
                  })
                : overview?.firstJoin?.delay < 0
                  ? i18n.t("recap.earlyArrivalDescription", {
                      earlyTime: formatDurationText(
                        Math.abs(overview?.firstJoin?.delay)
                      ),
                    })
                  : i18n.t("recap.noDelay"),
          },

          lastLeave: {
            ...overview?.lastLeave,
            time: overview?.lastLeave?.date
              ? formatTime(overview?.lastLeave?.date)
              : "--:--",
            overrunTime: formatDurationText(overview?.lastLeave?.overrun),
            description:
              overview?.lastLeave?.overrun > 0
                ? i18n.t("recap.lastJoinDescription", {
                    overrunTime: formatDurationText(
                      overview?.lastLeave?.overrun
                    ),
                  })
                : overview?.lastLeave?.overrun < 0
                  ? i18n.t("recap.earlyDepartureDescription", {
                      earlyTime: formatDurationText(
                        Math.abs(overview?.lastLeave?.overrun)
                      ),
                    })
                  : i18n.t("recap.noOverrun"),
          },

          participants: {
            ...overview?.participants,
            avgSpokeTime: formatDurationText(
              overview?.participants?.avgSpokeTime
            ),
            description: overview?.participants?.avgSpokeTime
              ? i18n.t("recap.totalAttendanceDescription", {
                  totalAttendanceTime: formatDurationText(
                    overview?.participants?.avgSpokeTime
                  ),
                })
              : i18n.t("recap.avgSpokeTime"),
          },

          engagement: {
            ...overview?.engagement,
            percentText: overview?.engagement?.engagementPercents
              ? `${overview?.engagement?.engagementPercents}%`
              : "-",
            speakingParticipants: overview?.engagement?.spokenUserCount || 0,
          },

          conductingStatusValues: {
            delayTime: formatDurationText(overview?.firstJoin?.delay),
            durationTime:
              formatDurationText((overview?.duration?.duration ?? 0) * 60) ||
              "-",
            engagementPercent: overview?.engagement?.engagementPercents || 0,
            speakingParticipants: overview?.engagement?.spokenUserCount || 0,
            totalParticipants: overview?.participants?.participantCount || 0,
          },
        };
      }
    )
  );

export const fetchDecisionsBySessionId = ({
  sessionId,
  includeDeleted,
}: {
  sessionId: string;
  includeDeleted?: boolean;
}) =>
  handleRequest(() =>
    DataProvider.getList(
      `v1/sessions/${sessionId}/decisions`,
      includeDeleted === undefined ? "" : { includeDeleted }
    ).then((r: any) => r || [])
  );

export const undoDeleteDecision = ({ id }: { id: string | number }) =>
  handleRequest(() => DataProvider.getList(`v1/decisions/${id}/undo-delete`));

export const modifyDecision = ({ id, sessionId, title }: ModifyDecisions) =>
  handleRequest(() => {
    if (id) {
      return DataProvider.update(`v1/decisions`, {
        id,
        title,
      });
    }

    return DataProvider.post(`v1/decisions`, { sessionId, title });
  });

export const getDecisionById = (id: string) =>
  handleRequest(() =>
    DataProvider.getOne(`v1/decisions`, { id }).then((r: any) => r || null)
  );

export const deleteDecision = ({ id }: { id: string | number }) =>
  handleRequest(() => DataProvider.delete(`v1/decisions/${id}`));
